(ns frontend.view.kpi-reporting.kpi-categories.creator
  (:require-macros [frontend.macros :refer [without-default-action-and-propagation]])
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.util :as cu :refer [flip]]
   [frontend.actions.kpi-category :as kpi-category-actions]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [ui.components.sortable-list.view :as sortable-list]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.components.form.view :as form]
   [frontend.view.kpi-reporting.kpi-categories.db :as db]
   [frontend.view.kpi-reporting.kpi-categories.events :as events]
   [frontend.view.kpi-reporting.kpi-categories.settings :as settings]
   [frontend.view.modals.components.input :as input]
   [frontend.view.modals.curtains.kpi-member-curtain.view :as kpi-member-curtain]
   [frontend.view.modals.events :as modals]
   [potpuri.core :as p :refer [fn-> fn->>]]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce state db/creator-state)

(defn user-guidance-element
  [{:keys [kw user-guidance category] :as view-args}]
  (let [placeholder (if (cu/separator-kw? kw)
                      (loc/tr :group-heading-placeholder)
                      (loc/tr :edit-guidance))]
    [form/view
     {:wrapper          (fu/cs "zap-form__light-grey__green__dark-grey kpi-user-guidance"
                               (when (cu/separator-kw? kw)
                                 "kpi-group-heading"))
      :placeholder      placeholder
      :type             "text"
      :edit?            true
      :empty-on-submit? true
      :value            user-guidance
      :on-change!       (partial events/update-guidance! category kw)
      :on-save!         (partial events/save-guidance! category kw)
      :cancel-edit!     (partial events/end-guidance-edit! category kw)
      :control-id       (str "kpi-user-guidance" kw)}]))

(defn to-js
  [m]
  (->> m
       (map (fn [[k v]]
              [(if (keyword? k)
                 (subs (str k) 1)
                 k)
               (if (keyword? v)
                 (subs (str v) 1)
                 v)]))
       (into {})))

(defn my-clj->js
  [x]
  (cond
    (string? x) x
    (keyword? x) (subs (str x) 1)
    (map? x) (reduce (fn [m [k v]]
                       (assoc m (my-clj->js k) (my-clj->js v)))
                     {}
                     x)
    (coll? x) (apply array (map my-clj->js x))
    :else x))

(def keywordize-kpi-vals
  #{:kw :type})

(defn kpi-to-clj
  [kpi]
  (let [kpi (js->clj kpi :keywordize-keys true)
        kpi (->> kpi
                 (map (fn [[k v]]
                        (if (contains? keywordize-kpi-vals k)
                          [k (keyword v)]
                          [k v])))
                 (into {}))]
    kpi))

(def keywordize-category-vals
  #{:kpi-category/kpis :kpi-category/questions
    :kpi-category/granularity :entity/type
    :kpi-category/currency})

(defn category-to-clj
  [category]
  (let [category (js->clj category :keywordize-keys true)
        category (->> category
                      (map (fn [[k v]]
                             (cond (contains? keywordize-category-vals k)
                                   [k
                                    (if (coll? v)
                                      (set (map keyword v))
                                      (keyword v))]
                                   (= k :kpi-category/ordering)
                                   [k (p/map-vals (partial map keyword) v)]
                                   :else
                                   [k v])))
                      (into {}))]
    category))

(defn render-kpi-item
  [{:keys [kpi category]}]
  (let [heading? (->> kpi
                      :kw
                      cu/separator-kw?)]
    [:div
     {:class-name (fu/cs "kpi-field"
                         (when heading?
                           "kpi-group-heading-field"))}
     [:i
      {:class "fas fa-grip-vertical ml-2"
       :title (loc/tr :drag-to-reorder)}]

     [:div.kpi-item-details
      (if heading?
        [:span.group-heading.d-none (loc/tr :group-heading-title)]
        [:span (fu/kpi-str kpi)])

      [:div
       [user-guidance-element
        {:kw            (:kw kpi)
         :user-guidance (->> kpi
                             :kw
                             (get (:kpi-category/user-guidances category)))
         :category      category}]]

      [button/square-secondary-open
       {:on-click (fn [_]
                    (if heading?
                      (events/remove-group-heading! (:type kpi) category (:kw kpi))
                      (events/toggle-kpi! category (:kw kpi) false)))
        :tooltip  (loc/tr :kpi/remove-kpi-tooltip)}
       [:i.fal.fa-times]]]]))

(defn kpis-view
  [{:keys [alerts category kpis toggle-kpi!]}]
  (let [bs-alert (:kpis alerts)
        category-kpis (set kpis)
        kpis (fu/all-kpis)
        ;;TODO add separator-kpi handling to frontend.utils(?)
        separator-kws (->> category-kpis
                           (filter cu/separator-kw?))
        separator-kpis (->> separator-kws
                            (map (fn [kw]
                                   {:kw kw})))
        selected-kpis (->> (concat kpis separator-kpis)
                           (filter (fn->> :kw category-kpis)))
        category-ordering (-> category
                              :kpi-category/ordering
                              :quantitative)
        ordered-kpis (->> (or @db/kpi-ordering category-ordering)
                          (filter (fn [kpi]
                                    (or (contains? category-kpis kpi)
                                        (cu/separator-kw? kpi)))))
        unordered-kpis (->> selected-kpis
                            (map :kw)
                            (remove (set ordered-kpis)))
        selected-kpi-kws (vec (concat ordered-kpis unordered-kpis))
        kw-ords (->> selected-kpi-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-kpis (->> selected-kpi-kws
                           (map (fn [kw]
                                  {:kw kw :type :quantitative :visible? true})))
        selected-kpis (->> selected-kpis
                           (sort-by (comp kw-ords :kw)))
        ords (comp kw-ords :kw)]
    [:div.kpi-wizard-section
     [:div.kpi-wizard-section-title
      (loc/tr :kpi-wizard/quantitative-title)]
     [:div
      {:class "px-4"}
      [:div
       [:div
        [:div.kpi-q-info
         (loc/tr :kpi-wizard/quantitative-info)]]]
      [:div
       [:div
        [:div.kpi-fields
         [sortable-list/view
          {:items selected-kpis
           :get-key :kw
           :on-reorder (fn [{:keys [source-index destination-index]}]
                         (events/reorder! :quantitative category selected-kpi-kws source-index destination-index))
           :render-item (fn [{:keys [item]}]
                          [render-kpi-item
                           {:kpi item
                            :category category}])}]]
        [:div
         [button/primary-open
          {:on-click (without-default-action-and-propagation
                      (events/open-kpi-curtain :quantitative category))}
          (loc/tr :kpi-manage)]]
        [:div
         [button/primary-open
          {:on-click (without-default-action-and-propagation
                      (events/add-group-heading! :quantitative category (vec selected-kpi-kws)))}
          (loc/tr :add-group-heading)]]]]]]))

(defn questions-view
  [{:keys [alerts category questions toggle-kpi!]}]
  (let [alert (:questions alerts)
        category-questions (set questions)
        questions (fu/all-kpi-questions)
        separator-kws (->> category-questions
                           (filter cu/separator-kw?))
        separator-questions (->> separator-kws
                                 (map (fn [kw]
                                        {:kw kw})))
        selected-questions (->> (concat questions separator-questions)
                                (filter (fn->> :kw category-questions)))
        category-ordering (-> category
                              :kpi-category/ordering
                              :qualitative)
        ordered-questions (->> (or @db/question-ordering category-ordering)
                               (filter (fn [q]
                                         (or (contains? category-questions q)
                                             (cu/separator-kw? q)))))
        unordered-questions (->> selected-questions
                                 (map :kw)
                                 (remove (set ordered-questions)))
        selected-question-kws (vec (concat ordered-questions unordered-questions))
        kw-ords (->> selected-question-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-questions (->> selected-question-kws
                                (map (fn [kw]
                                       {:kw kw :type :qualitative :visible? true})))
        selected-questions (->> selected-questions
                                (sort-by (comp kw-ords :kw)))
        ords (comp kw-ords :kw)]
    [:div.kpi-wizard-section
     [:div.kpi-wizard-section-title
      (loc/tr :kpi-wizard/qualitative-title)]
     [:div
      {:class "px-4"}
      [:div
       [:div
        {:class "px-4"}
        [:div.kpi-q-info
         (loc/tr :kpi-wizard/qualitative-info)]]]
      [:div
       [:div
        {:class "px-4"}
        [:div.kpi-fields
         [sortable-list/view
          {:items selected-questions
           :get-key :kw
           :on-reorder (fn [{:keys [source-index destination-index]}]
                         (events/reorder! :qualitative category selected-question-kws source-index destination-index))
           :render-item (fn [{:keys [item]}]
                          [render-kpi-item
                           {:kpi item
                            :category category}])}]]
        [:div.kpi-manage
         [button/primary-open
          {:on-click (without-default-action-and-propagation
                      (events/open-kpi-curtain :qualitative category))}
          (loc/tr :kpi-manage)]]
        [:div.kpi-add-group-heading
         [button/primary-open
          {:on-click (without-default-action-and-propagation
                      (events/add-group-heading! :qualitative
                                                 category
                                                 (vec selected-question-kws)))}
          (loc/tr :add-group-heading)]]]]]]))

(defn kpi-view
  [category state _view-args]
  (fn [category state _view-args]
    (let [{:keys [alerts]} @state
          alert (:category alerts)
          category-id (:db/id category)
          edit-category (-> @state
                            :edit-category
                            (get category-id))
          edit? (-> @state
                    :edit-category?
                    (get category-id))
          category-values (merge category edit-category)
          category-name (:kpi-category/name category-values)
          {:keys [kpi-category/granularity kpi-category/currency
                  kpi-category/forecast-requested?
                  kpi-category/kpis kpi-category/questions]}
          category-values]
      [:div.kpi-wizard-section-wrapper
       [:div.kpi-wizard-section
        [:div
         {:class-name "px-4"}
         [:div
          [:div

           [:div {:class-name "settings-company2__field"}
            [labelled-input/with-actions
             {:tools (clipboard/copy-tool currency currencies/value-options)}
             [labelled-input/view
              {:editor-type   :editor.type/select
               :data-type     :data.type/keyword
               :label         (loc/tr :currency)
               :is-clearable? false
               :value         currency
               :options       currencies/value-options
               :on-change     (fn [new-value & _]
                                (events/save-category!
                                 category
                                 :kpi-category/currency
                                 new-value
                                 (fn [_])
                                 (fn [_])))}]]]]]]]

       [kpis-view
        {:category    category
         :kpis        kpis
         :alerts      alerts
         :toggle-kpi! events/toggle-kpi!}]

       [questions-view
        {:category    category
         :questions   questions
         :alerts      alerts
         :toggle-kpi! events/toggle-kpi!}]])))

(defn kpi-selector
  []
  (let [processing? (:processing? state)
        kpi-category (events/get-wip-category)
        category-id (:db/id kpi-category)
        _ (when (and (not processing?)
                     (not kpi-category))
            (swap! state assoc :processing? true)
            (kpi-category-actions/*->create-empty!
             {}
             (fn []
               (js/setTimeout
                (fn [_]
                  (swap! state dissoc :processing?))
                2000))
             (fn [e]
               (swap! state dissoc :processing?))))
        selected-kpis (->> kpi-category
                           :kpi-category/kpis)
        selected-questions (->> kpi-category
                                :kpi-category/questions)
        button-disabled? (and (empty? selected-kpis)
                              (empty? selected-questions))]
    [:div.kpi-selector
     [:div.wizard-title
      (loc/tr :kpi-creator/step-template-title)]
     (when processing?
       [:div
        {:class-name (fu/cs :processing-layer (when processing? :processing))}
        [:div.processing-layer__dimmer]
        [:div.processing-layer__content
         [:div
          [:i.fal.fa-spinner.fa-pulse.fa-4x]
          [:span.processing-label (loc/tr :processing)]]]])
     [kpi-view kpi-category state {}]
     [:div.flex.gap-3.justify-center.my-3
      [button/secondary-hollow
       {:on-click (fn [_]
                    (swap! state dissoc :email-template-finished?))}
       (loc/tr :back)]
      [button/primary-solid
       {:disabled? button-disabled?
        :tooltip   (loc/tr :kpi-creator/kpi-selector-tooltip)
        :on-click  (fn [_]
                     (when-not button-disabled?
                       (kpi-category-actions/*->cleanup!
                        {:category-id category-id}
                        (fn []
                          (swap! state assoc :kpis-selected? true)
                          (swap! db/ordering dissoc :quantitative :qualitative)
                          (swap! state dissoc :processing))
                        (fn [e]
                          (logging/error (str "error! cleanup " e))
                          (swap! state dissoc :processing?)
                          ;;something went wrong, but allow proceeding
                          ;; (existence of empty group headings is not a critical situation)
                          (swap! state assoc :kpis-selected? true)))))}
       (loc/tr :kpi-creator/report-template-ok)]]]))

(defn text-field
  [{:keys [type entity attribute label placeholder on-save! validate! entity-atom]}]
  (let [resolved-entity-atom (or entity-atom
                                 (r/cursor session/entities [(:db/id entity)]))
        value (get entity attribute)
        default-on-save! (fn [attribute new-value on-success! on-error!]
                           (when (and (= new-value value) (fn? on-success!))
                             (on-success!))
                           (when-not (= new-value value)
                             (swap! resolved-entity-atom assoc attribute new-value)
                             (events/save-category!
                              entity
                              attribute
                              new-value
                              (fn [_]
                                (when on-success! (on-success!)))
                              (fn [err]
                                (when (= new-value (get @resolved-entity-atom attribute))
                                  (swap! resolved-entity-atom assoc attribute value))
                                (on-error! err)))))]
    [input/simple-text-field
     {:type        type
      :entity      entity
      :attribute   attribute
      :label       (or label (loc/tr attribute))
      :placeholder placeholder
      :validate!   validate!
      :on-save!    (or on-save! default-on-save!)}]))

(defn email-template
  []
  (let [state-atom (r/cursor db/creator-state [:state-atom])]
    (fn []
      (let [processing? (:processing? @state)
            category (events/get-wip-category)
            _ (when (and (not processing?)
                         (not category))
                (swap! state assoc :processing? true)
                (kpi-category-actions/*->create-empty!
                 {}
                 (fn []
                   (js/setTimeout
                    (fn [_]
                      (swap! state dissoc :processing?))
                    2000))
                 (fn [e]
                   (swap! state dissoc :processing?))))
            entity-atom (r/cursor session/entities [(:db/id category)])
            {:keys [kpi-category/email-title kpi-category/email-copy
                    kpi-category/email-greeting kpi-category/email-ending]}
            category
            validate! (fn [attribute val]
                        (swap! db/category assoc attribute val)
                        (when (empty? val)
                          (loc/tr :error-field-not-clearable [(loc/tr attribute)])))
            edit-category (merge category @db/category)
            temp-title (:kpi-category/email-title edit-category)
            temp-copy (:kpi-category/email-copy edit-category)
            temp-greeting (:kpi-category/email-greeting edit-category)
            temp-ending (:kpi-category/email-ending edit-category)
            disabled? (or (and (not (nil? temp-title))
                               (string/blank? temp-title))
                          (and (nil? temp-title)
                               (string/blank? email-title))
                          (and (not (nil? temp-copy))
                               (string/blank? temp-copy))
                          (and (nil? temp-copy)
                               (string/blank? email-copy))
                          (and (not (nil? temp-greeting))
                               (string/blank? temp-greeting))
                          (and (nil? temp-greeting)
                               (string/blank? email-greeting))
                          (and (not (nil? temp-ending))
                               (string/blank? temp-ending))
                          (and (nil? temp-ending)
                               (string/blank? email-ending)))]
        [:div.email-template
         [:div.wizard-title
          (loc/tr :kpi-creator/step-default-email-title)]
         (when processing?
           [:div
            {:class-name (fu/cs :processing-layer (when processing? :processing))}
            [:div.processing-layer__dimmer]
            [:div.processing-layer__content
             [:div
              [:i.fal.fa-spinner.fa-pulse.fa-4x]
              [:span.processing-label (loc/tr :processing)]]]])
         [settings/email-template
          {:category      category
           :edit-category edit-category
           :entity-atom   entity-atom
           :state-atom    state-atom
           :edit-state    db/category}]
         [:div.flex.gap-3.justify-center.my-3
          [button/secondary-hollow
           {:on-click (fn [_]
                        (reset! state nil)
                        (reset! db/wizard-state nil)
                        (swap! db/category dissoc
                               :kpi-category/email-title :kpi-category/email-copy
                               :kpi-category/email-greeting
                               :kpi-category/email-greeting-contact
                               :kpi-category/email-ending
                               :kpi-category/email-ending-contact))}
           (loc/tr :back)]
          [button/primary-solid
           {:disabled? disabled?
            :on-click  (fn [_]
                         (when-not disabled?
                           (swap! state assoc :email-template-finished? true)
                           (swap! db/category dissoc
                                  :kpi-category/email-title :kpi-category/email-copy
                                  :kpi-category/email-greeting
                                  :kpi-category/email-greeting-contact
                                  :kpi-category/email-ending
                                  :kpi-category/email-ending-contact)))}
           (loc/tr :kpi-wizard/confirm-email-template)]]]))))

(defn companies
  []
  (let [category (events/get-wip-category)
        category-id (:db/id category)
        session-entities @session/entities
        members (->> session-entities
                     vals
                     (cu/only-entity :entity.type/kpi-member)
                     (remove :common/disabled?)
                     (cu/only-entity :kpi-member/target category-id))
        persons (->> members
                     (map :kpi-member/person)
                     (map (fn->> (get session-entities))))
        role-accounts (->> session-entities
                           vals
                           (cu/only-entity :entity.type/role)
                           (remove (fn [r]
                                     (or (:common/disabled? r)
                                         (not (some #{(:role/role r)}
                                                    [:role/user :role/admin :role/zadmin
                                                     :role/sys-admin])))))
                           (map :role/subject)
                           set)
        our-contacts (->> members
                          (map :common/owner)
                          (map (fn->> (get session-entities))))
        companies (->> members
                       (map :kpi-member/asset)
                       (map (fn->> (get session-entities))))
        deleted-companies? (->> companies
                                (filter (fn [c]
                                          (or (nil? c)
                                              (:common/disabled? c))))
                                empty?
                                not)
        deleted-persons? (->> persons
                              (filter (fn [p]
                                        (or (nil? p)
                                            (:common/disabled? p))))
                              empty?
                              not)
        no-email-persons? (->> persons
                               (remove nil?)
                               (remove :common/disabled?)
                               (filter (fn [{:keys [person/universal-email-address] :as p}]
                                         (string/blank? universal-email-address)))
                               empty?
                               not)
        deleted-owners? (->> our-contacts
                             (filter (fn [o]
                                       (or (nil? o)
                                           (:common/disabled? o))))
                             empty?
                             not)
        deleted-owner-roles? (->> our-contacts
                                  (map :db/id)
                                  (remove (fn->> (contains? role-accounts)))
                                  empty?
                                  not)
        error-entities? (or deleted-companies?
                            deleted-persons?
                            no-email-persons?
                            deleted-owners?
                            deleted-owner-roles?)
        disabled? error-entities?]
    [:div.kpi-members
     [:div.wizard-title
      (loc/tr :kpi-creator/step-companies-title)]
     (if (empty? members)
       [:div.wizard-info.sm
        (loc/tr :kpi-wizard/companies-info)]
       [:div.members-list
        [:div.table-responsive
         [:table
          {:class-name (fu/cs "zapflow-table" "table-sm")}
          [:thead
           [:tr
            [:th (loc/tr :kpi-company)]
            [:th (loc/tr :their-contact)]
            [:th (loc/tr :our-contact)]
            [:th.member-actions ""]]]
          [:tbody
           (doall
            (for [member members
                  :let [company (->> member
                                     :kpi-member/asset
                                     (get @session/entities))
                        owner (->> member
                                   :common/owner
                                   (get @session/entities))
                        contact (->> member
                                     :kpi-member/person
                                     (get @session/entities))
                        deleted-contact? (or (nil? contact)
                                             (:common/disabled? contact))
                        no-email? (and (not deleted-contact?)
                                       (-> contact
                                           :person/universal-email-address
                                           empty?))]]
              ^{:key (:db/id member)}
              [:tr.kpi-member-row
               [:td [:p (entity/display-string company)]]
               [:td
                [:p (entity/display-string contact)
                 (when no-email? (str " " (loc/tr :kpi-error/no-email)))]]
               [:td [:p (entity/display-string owner)]]
               [:td.kpi-member-buttons
                [button/square-secondary-open
                 {:on-click (fn [_]
                              (events/remove-member! member))}
                 [:i.fal.fa-trash-alt]]
                #_[button/square-secondary-open
                   {:class-name ""
                    :on-click   (fn [_]
                                  (prn ">>>edit" (:db/id member)))}
                   [:i.fal.fa-pencil]]]]))]]
         (when deleted-companies?
           [alert/danger
            {}
            (loc/tr :kpi-error/deleted-companies-2)])
         (when deleted-persons?
           [alert/danger
            {}
            (loc/tr :kpi-error/deleted-persons-2)])
         (when no-email-persons?
           [alert/danger
            {}
            (loc/tr :kpi-error/no-email-persons-2)])
         (when (or deleted-owners?
                   deleted-owner-roles?)
           [alert/danger
            {}
            (loc/tr :kpi-error/deleted-accounts-2)])

         [button/primary-open
          {:class    "mt-2"
           :on-click (fn [_]
                       (modals/open-modal!
                        kpi-member-curtain/modal-key
                        {:target  category
                         :members members}))}
          (loc/tr :add-another-company)]]])
     [:div.flex.gap-3.justify-center.my-3
      [button/secondary-hollow
       {:on-click (fn [_]
                    (swap! state dissoc :kpis-selected?))}
       (loc/tr :back)]
      (when (empty? members)
        [button/primary-solid
         {:on-click (fn [_]
                      (modals/open-modal!
                       kpi-member-curtain/modal-key
                       {:target  category
                        :members members}))}
         (loc/tr :kpi-wizard/select-company-and-contacts)])
      (when-not (empty? members)
        [button/primary-solid
         {:disabled? disabled?
          :on-click  (fn [_]
                       (when-not disabled?
                         (swap! state assoc :companies-added? true)))}
         [:span
          [:span.d-none.d-md-inline-block (loc/tr :kpi-wizard/all-companies-added-1)]
          [:span (loc/tr :kpi-wizard/all-companies-added-2)]]])]]))

(defn category-name
  []
  (let [category (events/get-wip-category)
        temp-name (:kpi-category/name @db/category)
        disabled? (->> category
                       :kpi-category/name
                       string/blank?)                                                               ;;TODO remove?
        validate! (fn [attribute val]
                    (swap! db/category assoc attribute val)
                    (when (empty? val)
                      (loc/tr :error-field-not-clearable [(loc/tr attribute)])))
        disabled? (or (and (not (nil? temp-name))
                           (string/blank? temp-name))
                      (and (nil? temp-name)
                           (->> category
                                :kpi-category/name
                                string/blank?)))]
    [:div.category-name
     [:div.wizard-title
      (loc/tr :kpi-creator/step-category-name-title)]
     [:div.mb-3
      (loc/tr :kpi-wizard/category-name-info)]
     [:div.pb-3
      (loc/tr :for-example)
      [:ul.category-details-examples
       [:li (loc/tr :kpi-wizard/category-details-example-1)]
       [:li (loc/tr :kpi-wizard/category-details-example-2)]
       [:li (loc/tr :kpi-wizard/category-details-example-3)]]]

     [:div.wizard-step-card
      [text-field
       {:type        :text
        :entity      category
        :attribute   :kpi-category/name
        :label       (loc/tr :kpi-category/name)
        :validate!   validate!
        :placeholder (loc/tr :first-report-category-name-placeholder)}]]

     [:div.flex.gap-3.justify-center.my-3
      [button/secondary-hollow
       {:on-click (fn [_]
                    (swap! state dissoc :companies-added?)
                    (swap! db/category dissoc :kpi-category/name))}
       (loc/tr :back)]
      [button/primary-solid
       {:disabled? disabled?
        :on-click  (fn [_]
                     (when-not disabled?
                       (swap! state assoc :category-name-finished? true)
                       (swap! db/category dissoc :kpi-category/name)))}
       (loc/tr :kpi-wizard/category-details-ok)]]]))

(defn cycle-details
  []
  (fn []
    (let [now (js/Date.)
          current-year (.getFullYear now)
          year-options (->> (range (- current-year 7) (+ current-year 2))
                            reverse)
          first-cycle @db/first-cycle
            ;;cycle-name (:kpi-cycle/name first-cycle)
          target-month (:kpi-cycle/target-month first-cycle)
          target-year (:kpi-cycle/target-year first-cycle)
          disabled? (or                                                                           ;;(string/blank? cycle-name)
                     (not (number? target-month))
                     (not (number? target-year)))]
      [:div.cycle-details
       [:div.wizard-title
        (loc/tr :kpi-creator/step-cycle-name-title)]

       [:div.wizard-step-card
        [:div.wizard-info.text-left.sm.mt-3.mb-2
         (loc/tr :kpi-wizard/cycle-info-1)]
        [:div.wizard-info.text-left.sm.mt-3.mb-2
         (loc/tr :kpi-wizard/cycle-info-2)]
        [:div.wizard-info.text-left.sm.mt-3.mb-2
         (loc/tr :kpi-wizard/cycle-info-3)]
        [:div.wizard-info.text-left.sm.mt-3.mb-2
         (loc/tr :kpi-wizard/cycle-info-4)]
        [:div.first-target-month
         [labelled-input/view
          {:editor-type   :editor.type/select
           :data-type     :data.type/integer
           :label         (loc/tr :kpi-wizard/first-target-month)
           :is-clearable? false
           :menu-placement "top"
           :value         target-month
           :options       (->> (range 1 13)
                               (mapv (fn [n]
                                       {:value n
                                        :label (str n)})))
           :on-change     (fn [new-value & _]
                            (if (nil? new-value)
                              (swap! db/first-cycle dissoc :kpi-cycle/target-month)
                              (swap! db/first-cycle assoc :kpi-cycle/target-month new-value)))}]]

        [:div.first-target-year
         [labelled-input/view
          {:editor-type    :editor.type/select
           :data-type      :data.type/integer
           :label          (loc/tr :kpi-wizard/first-target-year)
           :is-clearable?  false
           :menu-placement "top"
           :value          target-year
           :options        (->> year-options
                                (mapv (fn [n]
                                        {:value n
                                         :label (str n)})))
           :on-change      (fn [new-value & _]
                             (if (nil? new-value)
                               (swap! db/first-cycle dissoc :kpi-cycle/target-year)
                               (swap! db/first-cycle assoc :kpi-cycle/target-year new-value)))}]]]

       [:div.flex.gap-3.justify-center.my-3
        [button/secondary-hollow
         {:on-click (fn [_]
                      (swap! state dissoc :category-name-finished?))}
         (loc/tr :back)]
        [button/primary-solid
         {:disabled? disabled?
          :on-click  (fn [_]
                       (when-not disabled?
                         (swap! state assoc :cycle-details-finished? true)))}
         (loc/tr :kpi-wizard/cycle-details-ok)]]])))

(defn confirm
  [{:keys [text state attribute]}]
  [:div
   {:class-name "d-flex"}
   [:div.zap-modal__body__row.mt-2.mb-3
    [:label
     {:class-name "zap-inlined-checkbox-label"}

     [:div.d-flex.justify-content-center.position-relative [:span.d-inline-block.pl-3.ml-3 text]
      [:input.delete-confirmation-modal__checkbox.zap-inlined-checkbox__green
       {:type      "checkbox"
        :checked   (boolean (get @state attribute))
        :on-change #(swap! state assoc
                           attribute
                           (-> %
                               .-target
                               .-checked))}]
      [:span
       {:class-name "checkmark"}]]]]])

(defn final-confirmation
  []
  (let [{:keys [kpi-cycle/target-year kpi-cycle/target-month]} @db/first-cycle
        category (events/get-wip-category)
        category-id (:db/id category)
        category-name (:kpi-category/name category)
        session-entities @session/entities
        members (->> session-entities
                     vals
                     (cu/only-entity :entity.type/kpi-member)
                     (remove :common/disabled?)
                     (cu/only-entity :kpi-member/target category-id))
        persons (->> members
                     (map :kpi-member/person)
                     (map (fn->> (get session-entities))))
        role-accounts (->> session-entities
                           vals
                           (cu/only-entity :entity.type/role)
                           (remove (fn [r]
                                     (or (:common/disabled? r)
                                         (not (some #{(:role/role r)}
                                                    [:role/user :role/admin :role/zadmin
                                                     :role/sys-admin])))))
                           (map :role/subject)
                           set)
        our-contacts (->> members
                          (map :common/owner)
                          (map (fn->> (get session-entities))))
        companies (->> members
                       (map :kpi-member/asset)
                       (map (fn->> (get session-entities))))
        deleted-companies? (->> companies
                                (filter (fn [c]
                                          (or (nil? c)
                                              (:common/disabled? c))))
                                empty?
                                not)
        deleted-persons? (->> persons
                              (filter (fn [p]
                                        (or (nil? p)
                                            (:common/disabled? p))))
                              empty?
                              not)
        no-email-persons? (->> persons
                               (remove nil?)
                               (remove :common/disabled?)
                               (filter (fn [{:keys [person/universal-email-address] :as p}]
                                         (string/blank? universal-email-address)))
                               empty?
                               not)
        deleted-owners? (->> our-contacts
                             (filter (fn [o]
                                       (or (nil? o)
                                           (:common/disabled? o))))
                             empty?
                             not)
        deleted-owner-roles? (->> our-contacts
                                  (map :db/id)
                                  (remove (fn->> (contains? role-accounts)))
                                  empty?
                                  not)
        error-entities? (or deleted-companies?
                            deleted-persons?
                            no-email-persons?
                            deleted-owners?
                            deleted-owner-roles?)
        processing? (:processing? @state)
        first-cycle @db/first-cycle
        ;;cycle-name (:kpi-cycle/name first-cycle)
        target-month (:kpi-cycle/target-month first-cycle)
        target-year (:kpi-cycle/target-year first-cycle)
        {:keys [confirm-1 confirm-2]} @db/confirms
        disabled? (or processing?
                      error-entities?
                      (empty? members)
                      ;;(string/blank? cycle-name)
                      (string/blank? category-name)
                      (string/blank? target-month)
                      (string/blank? target-year)
                      (not confirm-1)
                      (not confirm-2))]
    [:div.final-confirmation
     [:div.wizard-title
      (loc/tr :kpi-wizard/final-confirmation)]
     [:div.wizard-info.sm
      (loc/tr :kpi-wizard/confirmation-info)]

     (when processing?
       [:div
        {:class-name (fu/cs :processing-layer (when processing? :processing))}
        [:div.processing-layer__dimmer]
        [:div.processing-layer__content
         [:div
          [:i.fal.fa-spinner.fa-pulse.fa-4x]
          [:span.processing-label (loc/tr :processing)]]]])

     [:div.wizard-step-card
      [:div.general-settings
       [:div.wizard-heading
        (loc/tr :kpi-wizard/general-settings)]
       [:div.kpi-general-settings-row
        [:span (loc/tr :kpi-wizard/category-name-label) ": "] category-name]
       #_[:div.kpi-general-settings-row
          [:span (loc/tr :kpi-wizard/cycle-name-label) ": "] cycle-name]
       [:div.kpi-general-settings-row
        [:span (loc/tr :kpi-wizard/target-month) ": "] target-month "/" target-year]]
      [:div
       [:div.members-list
        [:div.wizard-heading
         (loc/tr :kpi-wizard/companies-in-cycle)]
        [:div.table-responsive
         [:table
          {:class-name (fu/cs "zap-table" "zap-table--bordered" "table-sm")}
          [:thead
           [:tr
            [:th.zap-table__headline (loc/tr :kpi-company)]
            [:th.zap-table__headline (loc/tr :their-contact)]
            [:th.zap-table__headline (loc/tr :our-contact)]]]
          [:tbody
           (doall
            (for
             [member members
              :let [company (->> member
                                 :kpi-member/asset
                                 (get session-entities))
                    company-deleted? (or (nil? company)
                                         (:common/disabled? company))
                    owner (->> member
                               :common/owner
                               (get session-entities))
                    owner-deleted? (or (nil? owner)
                                       (:common/disabled? owner)
                                       (not (contains? role-accounts (:db/id owner))))
                    contact (->> member
                                 :kpi-member/person
                                 (get session-entities))
                    contact-deleted? (or (nil? contact)
                                         (:common/disabled? contact))
                    contact-no-email? (string/blank? (:person/universal-email-address contact))]]
              ^{:key (:db/id member)}
              [:tr
               [:td.zap-table__cell
                {:class-name (when company-deleted?
                               "error")}
                (if company-deleted?
                  (loc/tr :kpi-error/deleted)
                  (entity/display-string company))]
               [:td.zap-table__cell
                {:class-name (when (or contact-deleted?
                                       contact-no-email?)
                               "error")}
                (cond contact-deleted?
                      (loc/tr :kpi-error/deleted)
                      contact-no-email?
                      (loc/tr :kpi-error/no-email)
                      :else
                      (entity/display-string contact))]
               [:td.zap-table__cell
                {:class-name (when owner-deleted?
                               "error")}
                (if owner-deleted?
                  (loc/tr :kpi-error/deleted)
                  (entity/display-string owner))]]))]]]]
       [:div.checkboxes-left-padding-zero
        [confirm
         {:text      (loc/tr :kpi-creator/confirmation-1)
          :state     db/confirms
          :attribute :confirm-1}]
        [confirm
         {:text      (loc/tr :kpi-wizard/confirmation-2)
          :state     db/confirms
          :attribute :confirm-2}]]
       (when deleted-companies?
         [alert/danger
          {}
          (loc/tr :kpi-error/deleted-companies)])
       (when deleted-persons?
         [alert/danger
          {}
          (loc/tr :kpi-error/deleted-persons)])
       (when no-email-persons?
         [alert/danger
          {}
          (loc/tr :kpi-error/no-email-persons)])
       (when (or deleted-owners?
                 deleted-owner-roles?)
         [alert/danger
          {}
          (loc/tr :kpi-error/deleted-accounts)])]
      [:div.flex.gap-3.justify-center.my-3
       [button/secondary-hollow
        {:on-click (fn [_]
                     (reset! db/confirms nil)
                     (swap! state dissoc :cycle-details-finished?))}
        (loc/tr :back)]
       [button/primary-solid
        {:disabled? disabled?
         :on-click  (fn [_]
                      (when-not disabled?
                        (let [msg {:category-id category-id
                                   :kpi-cycle   {:target-year  target-year
                                                 :target-month target-month}}]
                          (swap! state assoc :processing? true)
                          (kpi-category-actions/*->finalize!
                           msg
                           (fn [_]
                             ;;(swap! state dissoc :processing? :show?)
                             (reset! state nil)
                             (reset! db/wizard-state nil)
                             (reset! db/state nil))
                           (fn [e]
                             (swap! state dissoc :processing?))))))}
        (loc/tr :confirm)]]]]))

(defn view
  [{:keys [kpi-category]}]
  (let [;;wizard-status (:tenant/kpi-category-wizard-status @session/tenant)
        {:keys [info-seen? video-seen? kpis-selected? email-template-finished?
                companies-added? category-name-finished? cycle-details-finished?]}
        @state]
    [:div.kpi-category-wizard.entity__container
     [:div
      {:class-name (fu/cs "kpi-category-wizard-inner"
                          (when (not email-template-finished?)
                            "wizard-inner-email-template"))}
      (cond                                                                                         ;;(not info-seen?)
        ;;[initial-info]
        ;;(not video-seen?)
        ;;[video]
        (not email-template-finished?)
        [email-template]
        (not kpis-selected?)
        [kpi-selector]
        (not companies-added?)
        [companies]
        (not category-name-finished?)
        [category-name]
        (not cycle-details-finished?)
        [cycle-details]
        :else
        [final-confirmation]
        :else [:div
               "Something went wrong"
               [:div.flex.gap-3.justify-center.my-3
                [button/secondary-hollow
                 {:on-click (fn [_]
                              (swap! state dissoc
                                     :video-seen?
                                     :kpis-selected?
                                     :email-template-finished?
                                     :companies-added?
                                     :category-name-finished?))}
                 "Start over"]]])]]))
